import React from 'react';
import { Question, Service } from '../product/context-model';
import { PerformerType } from '../../constants/performer.constants';
import { getAdditionalFormFieldsValues } from './enrollment-context-additions';
import { ContextService } from '../product/context';
import { EnrollmentResponse, requestStatus } from '../api';
import Stack from '../../../utilities/stack';
import { ConsentIDs } from '../../constants/service.constants';

type ParamValueType = object | string | Date | number | boolean;

type Param = {
	name: string;
	value: ParamValueType;
};

type isQualifiedInput = {
	id: string;
	value: string;
	operator?: string;
};

export type AnswerValueProp = boolean | string | string[];

type AnswerProp = {
	id: string;
	title: string;
	type: 'date' | 'boolean' | 'choice';
	answer: Date | AnswerValueProp;
	params: Param[];
};

export abstract class InputQuestion {
	id: string;
	title: string;
	type: 'date' | 'boolean' | 'choice';
	answer: Date | AnswerValueProp;
	constructor(answer?: AnswerProp) {
		if (answer) {
			this.id = answer.id;
			this.title = answer.title;
			this.answer = answer.answer;
			this.type = answer.type;
		} else {
			this.id = '';
			this.title = '';
			this.answer = '';
			this.type = 'boolean';
		}
	}
	isQualified(questions?: isQualifiedInput[]): boolean {
		if (!questions || !questions.length) {
			return true;
		}
		const condition = questions.find((answer) => answer.id === this.id);
		if (condition?.operator === 'not') {
			// condition "not" will accept any value except specified, or if value was not specified in previous answers
			return this.answer !== condition.value;
		}
		return !condition || this.answer === condition.value;
	}
}

export class DateQuestion extends InputQuestion {
	isQualified(questions?: isQualifiedInput[]): boolean {
		if (!questions || !questions.length) {
			return true;
		}
		return questions.some((question) => {
			if (question.id === this.id) {
				if (!question.value) {
					return true;
				}
				const datedAnswer = new Date(this.answer as string);
				const floorYear = 1970;
				const years = Math.abs(
					new Date(new Date().getTime() - datedAnswer.getTime()).getFullYear() -
						floorYear
				);
				return years >= +question.value;
			}
			return false;
		});
	}
}

export class BooleanQuestion extends InputQuestion {}

export class ChoiceQuestion extends InputQuestion {}

export class MultiChoiceQuestion extends InputQuestion {
	isQualified(questions: isQualifiedInput[]): boolean {
		return questions.every((question) => {
			if (question.id !== this.id) {
				return true;
			}
			return (this.answer as string[]).includes(question.value);
		});
	}
}

type InitialForm = {
	fields: {
		name: string;
		label: string;
		service?: string;
		question?: string;
	}[];
};
type UserInput = {
	performer: 'patient' | 'caregiver';
	questions: InputQuestion[];
	services: string[];
	consents: string[];
	form: {
		id: string;
		value: Date | AnswerValueProp | undefined;
		question?: string;
	}[];
	isFormValid: boolean;
	discardedServices: string[];
};
export enum UserInputType {
	CONSENTS = 'consents',
	FORMS = 'form',
	PERFORMER = 'performer',
	QUESTIONS = 'questions',
	SERVICES = 'services',
}

export class EnrollmentContextService {
	enrollmentResponse: EnrollmentResponse = {
		patientId: undefined,
		status: requestStatus.INACTIVE,
		data: [],
		time: 0,
	};
	input: UserInput;
	overrideQuestions: { [key: string]: Question[] } = {};
	isCopayText = false;
	serviceDisclaimers: Set<string> = new Set();

	private readonly initialForms: InitialForm[] = [];

	constructor(forms?: InitialForm[]) {
		this.input = {
			performer: PerformerType.PATIENT,
			questions: [],
			services: [],
			consents: [],
			form: [],
			isFormValid: false,
			discardedServices: [],
		};
		if (forms) {
			this.initialForms = forms;
		}
		this.resetInput();
	}

	addServiceDisclaimer(serviceDisclaimer: string): void {
		this.serviceDisclaimers.add(serviceDisclaimer);
	}
	resetInput(input?: UserInputType) {
		if (input) {
			switch (input) {
				case UserInputType.PERFORMER:
					this.input.performer = 'patient';
					break;
				case UserInputType.FORMS:
					this.input.isFormValid = false;
					this.input.form = [];
					if (this.initialForms) {
						this.initialForms.forEach((form) =>
							this.input.form.push(
								...form.fields.map((serviceField) => {
									return {
										id: serviceField.name,
										value: '',
										question: serviceField.question,
									};
								})
							)
						);
					}
					this.removeConsent(ConsentIDs.hipaa);
					this.removeConsent(ConsentIDs.tcpa);
					break;
				case UserInputType.SERVICES:
					this.input.services = [];
					this.input.discardedServices = [];
					break;
				default:
					this.input[input] = [];
			}
		} else {
			this.resetInput(UserInputType.CONSENTS);
			this.resetInput(UserInputType.FORMS);
			this.resetInput(UserInputType.PERFORMER);
			this.resetInput(UserInputType.QUESTIONS);
			this.resetInput(UserInputType.SERVICES);
		}
	}

	removeService(serviceId: string) {
		const position = this.input.services.indexOf(serviceId);
		if (position > -1) {
			this.input.services.splice(position, 1);
		}
	}
	addService(serviceId: string) {
		if (this.input.services.indexOf(serviceId) === -1) {
			this.input.services.push(serviceId);
		}
	}
	addDiscardedService(serviceId: string | string[]) {
		if (Array.isArray(serviceId)) {
			for (const service of serviceId) {
				if (this.input.discardedServices.indexOf(service) === -1) {
					this.input.discardedServices.push(service);
				}
			}
		} else {
			if (this.input.discardedServices.indexOf(serviceId as string) === -1) {
				this.input.discardedServices.push(serviceId as string);
			}
		}
	}
	removeDiscardedService(serviceId: string | string[]) {
		if (Array.isArray(serviceId)) {
			for (const service of serviceId) {
				const pos = this.input.discardedServices.indexOf(service);
				if (pos !== -1) {
					this.input.discardedServices.splice(pos, 1);
				}
			}
		} else {
			const pos = this.input.discardedServices.indexOf(serviceId as string);
			if (pos !== -1) {
				this.input.discardedServices.splice(pos, 1);
			}
		}
	}
	removeConsent(consentId: string) {
		const position = this.input.consents.indexOf(consentId);
		if (position > -1) {
			this.input.consents.splice(position, 1);
		}
	}
	addConsent(consent: string) {
		if (!this.input.consents.includes(consent)) {
			this.input.consents.push(consent);
		}
	}
	getFormField(id: string) {
		return this.input.form.find((formField) => formField.id === id);
	}
	getFormFieldsValues(): { [key: string]: ParamValueType } {
		return {
			firstname: this.getFormField('firstname')?.value as string,
			lastname: this.getFormField('lastname')?.value as string,
			firstnamecaregiver: this.getFormField('firstnamecaregiver')
				?.value as string,
			lastnamecaregiver: this.getFormField('lastnamecaregiver')
				?.value as string,
			dob: this.getFormField('dob')?.value as string,
			gender: this.getFormField('gender')?.value as string,
			gendercaregiver: this.getFormField('gendercaregiver')?.value as string,
			medication: this.getFormField('medication')?.value as string,
			email: this.getFormField('email')?.value as string,
			mail: this.getFormField('mail')?.value as string,
			apt: this.getFormField('apt')?.value as string,
			city: this.getFormField('city')?.value as string,
			state: this.getFormField('state')?.value as string,
			mailcaregiver: this.getFormField('mailcaregiver')?.value as string,
			aptcaregiver: this.getFormField('aptcaregiver')?.value as string,
			citycaregiver: this.getFormField('citycaregiver')?.value as string,
			statecaregiver: this.getFormField('statecaregiver')?.value as string,
			phone: this.getFormField('phone')?.value as string,
			mobile: this.getFormField('mobile')?.value as string,
			phoneusage: this.getFormField('phoneusage')?.value as string,
			zip: this.getFormField('zip')?.value as string,
			zipcaregiver: this.getFormField('zipcaregiver')?.value as string,
			consenttcpa: this.getFormField('consenttcpa')?.value as boolean,
			consenthipaa: this.getFormField('consenthipaa')?.value as string,
			bestCommunication: this.getFormField('bestCommunication')
				?.value as string,
			bestTimeToConnect: this.getFormField('bestTimeToConnect')
				?.value as string,
			checkedSameAddress: this.getFormField('checkedSameAddress')
				?.value as string,
			...getAdditionalFormFieldsValues(this),
		};
	}
	processFormFields() {
		const fields = this.getFormFieldsValues();
		const keys = Object.keys(fields);

		for (const key of keys) {
			try {
				if (typeof fields[key] === 'string') {
					const value = (fields[key] as string)?.trim();
					this.updateFormField({ id: key, answer: value });
				}
			} catch (err) {
				console.error(err);
			}
		}
	}

	updateFormField(input: {
		id: string;
		answer: Date | AnswerValueProp | undefined;
	}) {
		const field = this.input.form.find(
			(formField) => formField.id === input.id
		);
		if (field) {
			field.value = input.answer;
		} else {
			this.input.form.push({ id: input.id, value: input.answer });
		}
	}
	answerQuestion(answer: AnswerProp) {
		const answerIndex = this.input.questions.findIndex(
			(question) => question.id === answer.id
		);
		if (answerIndex === -1) {
			switch (answer.type) {
				case 'date':
					this.input.questions.push(new DateQuestion(answer));
					break;
				case 'choice':
					this.input.questions.push(new ChoiceQuestion(answer));
					break;
				default:
					this.input.questions.push(new BooleanQuestion(answer));
					break;
			}
		} else if (this.input.questions[answerIndex].answer !== answer.answer) {
			this.input.questions.splice(answerIndex + 1);
			this.input.questions[answerIndex].answer = answer.answer;
		}
	}

	getQuestion(questionId: string) {
		return this.input.questions.find((question) => question.id === questionId);
	}

	isServiceEligible(service: Service): boolean {
		return service.isEligible(
			this.input.performer,
			this.input.questions,
			this.input.consents,
			this.input.discardedServices
		);
	}

	eligibleServices(context: ContextService): Service[] {
		return context.json.services.filter((service) =>
			service.isEligible(
				this.input.performer,
				this.input.questions,
				this.input.consents,
				this.input.discardedServices
			)
		);
	}

	isStatic(ctxt: ContextService) {
		return !ctxt.json.services.find((service) => {
			return this.isServiceEligible(service) && !service.static;
		});
	}

	isFastTrack(ctxt: ContextService) {
		return !ctxt.json.services.find((service) => {
			return (
				this.isServiceEligible(service) &&
				!(service.static || service.fasttrack)
			);
		});
	}
}
export const enrollmentContextConst = new EnrollmentContextService();
export const enrollmentContext = React.createContext(enrollmentContextConst);
export const EnrollmentContextProvider = enrollmentContext.Provider;
